import { configureStore, createSlice } from '@reduxjs/toolkit';
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import axios from 'axios';
import { combineReducers } from 'redux';

const uiSlice = createSlice({
    name: 'ui',
    initialState: {
        sidebarShow: true,
        sidebarUnfoldable: false,
    },
    reducers: {
        update: (state, action) => {
            localStorage.setItem(action.type, JSON.stringify(action.payload));
            return state;
        },
    },
});

export const { update } = uiSlice.actions;

const chatroomMessagesSlice = createSlice({
    name: 'chatroomMessages',
    initialState: { data: {} },
    reducers: {
        setWithChatroomID: (state, action) => {
            const { chatroom_id, data } = action.payload;
            state.data[chatroom_id] = data;
        },
        addWithChatroomID: (state, action) => {
            const { chatroom_id, data } = action.payload;
            if (state.data[chatroom_id]) {
                state.data[chatroom_id] = [data, ...state.data[chatroom_id]];
            } else {
                state.data[chatroom_id] = [data];
            }
        },
    },
});

export const { setWithChatroomID, addWithChatroomID } = chatroomMessagesSlice.actions;
export const ChatroommessagesSetWithChatroomID = (chatroomID, data) => ({ type: chatroomMessagesSlice.actions.setWithChatroomID.type, payload: { chatroom_id: chatroomID, data } });
export const ChatroommessagesAddWithChatroomID = (chatroomID, data) => ({ type: chatroomMessagesSlice.actions.addWithChatroomID.type, payload: { chatroom_id: chatroomID, data } });

const aiMessagesSlice = createSlice({
    name: 'aiMessages',
    initialState: { data: {} },
    reducers: {
        setWithAIcallID: (state, action) => {
            const { aicall_id, data } = action.payload;
            state.data[aicall_id] = data;
        },
        addWithAIcallID: (state, action) => {
            const { aicall_id, data } = action.payload;
            state.data[aicall_id] = [data, ...(state.data[aicall_id] || [])];
        },
    },
});

export const { setWithAIcallID, addWithAIcallID } = aiMessagesSlice.actions;
export const AImessagesSetWithAIcallID = (aicallID, data) => ({ type: aiMessagesSlice.actions.setWithAIcallID.type, payload: { aicall_id: aicallID, data } });
export const AImessagesAddWithAIcallID = (aicallID, data) => ({ type: aiMessagesSlice.actions.addWithAIcallID.type, payload: { aicall_id: aicallID, data } });

const transcriptsSlice = createSlice({
    name: 'transcripts',
    initialState: { data: {} },
    reducers: {
        setWithTranscribeID: (state, action) => {
            const { chatroom_id, data } = action.payload;
            state.data[chatroom_id] = data;
        },
        addWithTranscribeID: (state, action) => {
            const { chatroom_id, data } = action.payload;
            const roomID = data.transcribe_id;
            if (state.data[chatroom_id]) {
                state.data[chatroom_id] = [data, ...state.data[chatroom_id]];
            } else {
                state.data[chatroom_id] = [data];
            }
        },
    },
});

export const { setWithTranscribeID, addWithTranscribeID } = transcriptsSlice.actions;
export const TranscriptsSetWithTranscribeID = (transcribeID, data) => ({ type: transcriptsSlice.actions.setWithTranscribeID.type, payload: { chatroom_id: transcribeID, data } });
export const TranscriptsAddWithTranscribeID = (transcribeID, data) => ({ type: transcriptsSlice.actions.addWithTranscribeID.type, payload: { chatroom_id: transcribeID, data } });

const currentCallSlice = createSlice({
    name: 'currentCall',
    initialState: {
        session: {},
        id: "",
        source: "",
        destination: "",
        status: "finished",
        direction: "",
    },
    reducers: {
        setSession: (state, action) => {
            const session = action.payload;
            state.session = session;
            state.id = session.id;
            state.direction = session.direction;
            if (session.remote_identity == null) {
                return state;
            }
            if (state.direction === 'incoming') {
                state.source = session.remote_identity.uri.user;
                state.destination = session.local_identity.uri.user;
                state.status = 'ringing';
            } else {
                state.source = session.local_identity.uri.user;
                state.destination = session.remote_identity.uri.user;
                state.status = 'dialing';
            }
        },
        setStatus: (state, action) => {
            state.status = action.payload;
        },
    },
});

export const { setSession, setStatus } = currentCallSlice.actions;
export const CurrentcallSetSession = (session) => ({ type: currentCallSlice.actions.setSession.type, payload: session });
export const CurrentcallSetStatus = (state) => ({ type: currentCallSlice.actions.setStatus.type, payload: state });

const authSlice = createSlice({
    name: 'auth',
    initialState: {
        agentInfo: null,
        token: null,
    },
    reducers: {
        setAgentInfo: (state, action) => {
            state.agentInfo = action.payload;
            localStorage.setItem("agent_info", JSON.stringify(action.payload));
        },
        setToken: (state, action) => {
            state.token = action.payload;
            localStorage.setItem("token", action.payload);
        },
        clear: (state) => {
            state.agentInfo = null;
            state.token = null;
            localStorage.removeItem("agent_info");
            localStorage.removeItem("token");
        },
    },
});

export const { setAgentInfo, setToken, clear } = authSlice.actions;
export const AuthSetAgentInfo = (agentInfo) => ({ type: authSlice.actions.setAgentInfo.type, payload: agentInfo });
export const AuthSetToken = (token) => ({ type: authSlice.actions.setToken.type, payload: token });
export const AuthClear = () => ({ type: authSlice.actions.clear.type });

const conversationMessagesSlice = createSlice({
    name: 'conversationMessages',
    initialState: { data: {} },
    reducers: {
        setWithConversationId: (state, action) => {
            const { conversation_id, data } = action.payload;
            state.data[conversation_id] = data;
        },
        addWithConversationId: (state, action) => {
            const { conversation_id, data } = action.payload;
            if (state.data[conversation_id]) {
                state.data[conversation_id] = [...state.data[conversation_id], data];
            } else {
                state.data[conversation_id] = [data];
            }
        },
    },
});

export const { setWithConversationId, addWithConversationId } = conversationMessagesSlice.actions;
export const ConversationmessagesSetWithConversationID = (conversationID, data) => ({ type: conversationMessagesSlice.actions.setWithConversationId.type, payload: { conversation_id: conversationID, data } });
export const ConversationmessagesAddWithConversationID = (conversationID, data) => ({ type: conversationMessagesSlice.actions.addWithConversationId.type, payload: { conversation_id: conversationID, data } });

export const fetchAgentInfo = () => async (dispatch, getState) => {
    const token = getState().auth.token;
    if (!token) return;
    try {
        const response = await axios.get("https://api.voipbin.net/v1.0/me", {
            headers: { Authorization: `Bearer ${token}` },
        });
        dispatch(setAgentInfo(response.data));
    } catch (error) {
        console.error("Failed to fetch agent info", error);
    }
};

const rootReducer = combineReducers({
    ui: uiSlice.reducer,
    chatroomMessages: chatroomMessagesSlice.reducer,
    aiMessages: aiMessagesSlice.reducer,
    currentCall: currentCallSlice.reducer,
    transcripts: transcriptsSlice.reducer,
    auth: authSlice.reducer,
    conversationMessages: conversationMessagesSlice.reducer,
});

const persistConfig = {
    key: 'root',
    storage,
    whitelist: ['auth'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
    reducer: persistedReducer,
    middleware: (getDefaultMiddleware) =>
        getDefaultMiddleware({
            serializableCheck: {
                ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
            },
        }),
});

const persistor = persistStore(store);

export { store, persistor };
export default store;